import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';
import AuthContext from '../contexts/AuthContext';
import logo from '../logo.jpg';

const MIN_PASSWORD = 8;

/**
 * 회사를 새로 등록하며 가입하는 화면.
 *
 * 예전 회원가입 화면은 계정만 만들었고, 그 계정에는 회사가 붙지 않아 들어가도
 * 빈 화면만 보였다. 여기서는 회사가 함께 만들어지고 적은 사람이 그 회사의
 * 소유자가 된다.
 *
 * **이 문은 처음 시작하는 회사만 쓴다.** 이미 있는 회사에 들어가는 길은 초대
 * 링크뿐이다. 그 갈림을 흐리게 두면, 링크를 잃어버린 직원이 자기 회사와 이름만
 * 같은 회사를 하나 더 만들고 그 안에 혼자 남는다 -- 본인은 앱이 비어 있다고
 * 느끼고, 관리자는 그 사람을 찾지 못한다. 그래서 화면 맨 위에서 한 번,
 * 단추 아래에서 한 번 더 갈라 놓는다.
 */
const SignUpPage = () => {
    const navigate = useNavigate();
    const { login } = useContext(AuthContext);

    const [companyName, setCompanyName] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [again, setAgain] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!companyName.trim()) {
            setError('회사 이름을 적어 주세요.');
            return;
        }
        if (password.length < MIN_PASSWORD) {
            setError(`비밀번호는 ${MIN_PASSWORD}자 이상으로 정해 주세요.`);
            return;
        }
        if (password !== again) {
            setError('두 번 적은 비밀번호가 다릅니다.');
            return;
        }
        setError('');
        setSaving(true);
        try {
            const { data } = await api.post('/auth/signup', {
                companyName,
                name,
                email,
                password,
            });
            // 서버가 토큰까지 돌려준다. 방금 정한 것을 다시 적게 할 이유가 없다.
            await login(data.access_token, data.refresh_token);
            navigate('/', { replace: true });
        } catch (err) {
            setError(err.response?.data?.message || '가입하지 못했습니다.');
            setSaving(false);
        }
    };

    return (
        <div className="d-flex align-items-center justify-content-center min-vh-100 bg-light px-3 py-5">
            <div className="bg-white rounded-4 border p-4 w-100" style={{ maxWidth: '420px' }}>
                <div className="text-center mb-4">
                    <img src={logo} alt="바로보고" height="44" className="rounded mb-3" />
                    <h5 className="fw-bold mb-1">우리 회사를 새로 등록합니다</h5>
                    <p className="text-muted small mb-0">
                        적으신 분이 이 회사의 소유자가 되고, 이후 함께 일할 분들을
                        초대하시면 됩니다.
                    </p>
                </div>

                {/* 잘못 들어온 사람을 여기서 돌려보낸다. 가입을 마치고 나면
                    되돌리는 데 관리자 손이 필요하다. */}
                <div className="alert alert-light border py-2 small">
                    <i className="bi bi-info-circle me-1"></i>
                    이미 <strong>바로보고를 쓰는 회사</strong>의 직원이시라면 여기가
                    아닙니다. 관리자에게 <strong>초대 링크</strong>를 요청하세요.
                </div>

                {error && <div className="alert alert-warning py-2 small">{error}</div>}

                <form onSubmit={submit}>
                    <div className="mb-3">
                        <label className="form-label fw-bold small">회사 이름</label>
                        <input
                            className="form-control"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            placeholder="선흥"
                            autoFocus
                        />
                        <div className="form-text">나중에 설정에서 바꿀 수 있습니다.</div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold small">이름</label>
                        <input
                            className="form-control"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="홍길동"
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold small">이메일</label>
                        <input
                            type="email"
                            className="form-control"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="name@example.com"
                        />
                        <div className="form-text">이 주소로 로그인합니다.</div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold small">비밀번호</label>
                        <input
                            type="password"
                            className="form-control"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={`${MIN_PASSWORD}자 이상`}
                        />
                    </div>
                    <div className="mb-4">
                        <label className="form-label fw-bold small">비밀번호 확인</label>
                        <input
                            type="password"
                            className="form-control"
                            value={again}
                            onChange={(e) => setAgain(e.target.value)}
                        />
                    </div>
                    <button
                        type="submit"
                        className="btn btn-primary w-100 fw-bold"
                        disabled={saving}
                    >
                        {saving ? '만드는 중…' : '회사 등록하고 시작하기'}
                    </button>
                </form>

                <div className="text-center mt-3">
                    <Link
                        to="/login"
                        className="text-decoration-none text-muted"
                        style={{ fontSize: '0.8rem' }}
                    >
                        이미 계정이 있으신가요? 로그인
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default SignUpPage;
